import React, { useState, useEffect } from 'react';
import { AuthService } from '../services/auth';
import proxyapi from '../services/ProxyApi';
import EmailInput from '../components/EmailInput';

const auth = new AuthService();

function UpdateEmail() {
  const [currentEmail, setCurrentEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);

  useEffect(() => {
    // Fetch current email on component mount
    const fetchCurrentEmail = async () => {
      try {
        const attributes = await auth.fetchUserAttributes();
        if (attributes.email) {
          setCurrentEmail(attributes.email);
        }
      } catch (err) {
        console.error('Error fetching user attributes:', err);
      }
    };

    fetchCurrentEmail();
  }, []);

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    
    if (newEmail === currentEmail) {
      setError('New email must be different from your current email');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setIsLoading(true);
      const result = await auth.updateEmail(newEmail);
      
      if (result.nextStep.updateAttributeStep === 'CONFIRM_ATTRIBUTE_WITH_CODE') {
        setNeedsConfirmation(true);
      } else {
        setSuccess(true);
      }
    } catch (err) {
      if (err.code === 'AliasExistsException') {
        setError('This email address is already in use');
      } else if (err.code === 'LimitExceededException') {
        setError('Too many attempts. Please try again later.');
      } else {
        setError(err.message || 'Failed to update email. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const syncEmailWithDatabase = async (email) => {
    try {
      await proxyapi.put('/api/v1/users/email', { email });
      console.log('Email successfully synced with database');
    } catch (error) {
      console.warn('Database email sync failed:', error);
      throw error;
    }
  };

  const handleConfirmEmail = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!confirmationCode.trim()) {
      setError('Please enter the confirmation code');
      return;
    }

    try {
      setIsLoading(true);
      
      // First confirm the email update in AWS Amplify
      await auth.confirmEmailUpdate(confirmationCode);
      
      // Then sync the email with your MongoDB database using the safe endpoint
      try {
        await syncEmailWithDatabase(newEmail);
      } catch (syncError) {
        console.warn('AWS email updated but database sync failed:', syncError);
        // Don't fail the whole operation - AWS is updated successfully
      }
      
      setSuccess(true);
      setNeedsConfirmation(false);
      setCurrentEmail(newEmail);
      setNewEmail('');
      setConfirmationCode('');
    } catch (err) {
      if (err.code === 'CodeMismatchException') {
        setError('Invalid verification code. Please try again.');
      } else if (err.code === 'ExpiredCodeException') {
        setError('Verification code has expired. Please request a new one.');
      } else {
        setError(err.message || 'Failed to confirm email update. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setNeedsConfirmation(false);
    setNewEmail('');
    setConfirmationCode('');
    setError(null);
  };

  return (
    <div className="bg-white shadow rounded-lg p-6 mb-6">
      <h2 className="text-xl font-semibold mb-4">Update Email Address</h2>
      
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {needsConfirmation 
            ? 'Verification code sent to your new email address.' 
            : 'Email address updated successfully!'}
        </div>
      )}
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {!needsConfirmation ? (
        <form onSubmit={handleUpdateEmail}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Current Email
            </label>
            <div className="px-3 py-2 bg-gray-100 rounded text-gray-700">
              {currentEmail || 'Loading...'}
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="newEmail">
              New Email Address
            </label>
            <EmailInput
              id="newEmail"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Enter your new email address"
              required
            />
          </div>
          
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-black text-white px-4 py-2 rounded hover:bg-gray-800 disabled:bg-gray-400"
          >
            {isLoading ? 'Updating...' : 'Update Email'}
          </button>
        </form>
      ) : (
        <form onSubmit={handleConfirmEmail}>
          <div className="mb-4">
            <p className="text-gray-700 mb-4">
              A verification code has been sent to <strong>{newEmail}</strong>. 
              Please check your inbox and enter the code below to confirm your new email address.
            </p>
            
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="confirmationCode">
              Verification Code
            </label>
            <input
              id="confirmationCode"
              type="text"
              value={confirmationCode}
              onChange={(e) => setConfirmationCode(e.target.value)}
              placeholder="Enter verification code"
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              required
            />
          </div>
          
          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-black text-white px-4 py-2 rounded hover:bg-gray-800 disabled:bg-gray-400"
            >
              {isLoading ? 'Verifying...' : 'Verify Code'}
            </button>
            
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default UpdateEmail;